import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from '../../../modals/base-modal/base-modal.component';
import { Globals } from '../../../../global/globals';

interface PaperInfo {
  id: number;
  title: string;
  authors: string | string[];
  doi: string | null;
  abstractText: string | null;
  venue: string | null;
  publicationDate: string | null;
  workType: string | null;
  sourceType: string | null;
  externalId: string | null;
  pdfUrl: string | null;
  projectsLinkedCount: number;
}

const DOI_REGEX = /10\.\d{4,9}\/.+/;

@Component({
  selector: 'app-paper-info-modal',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './paper-info-modal.component.html',
})
export class PaperInfoModalComponent {
  private http = inject(HttpClient);
  private globals = inject(Globals);

  private _paperToProjectId = signal<number | null>(null);
  private _projectId = signal<string>('');

  readonly paper = signal<PaperInfo | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly isOpen = computed(() => this._paperToProjectId() !== null);

  @Input()
  set paperToProjectId(value: number | null) {
    this._paperToProjectId.set(value);
    if (value !== null && this._projectId()) {
      this.fetchPaper();
    }
  }

  @Input()
  set projectId(value: string) {
    this._projectId.set(value);
    if (value && this._paperToProjectId() !== null) {
      this.fetchPaper();
    }
  }

  @Output() closed = new EventEmitter<void>();
  @Output() viewAuditTrail = new EventEmitter<number>();

  emitViewAuditTrail(): void {
    const ptpId = this._paperToProjectId();
    if (ptpId !== null) {
      this.viewAuditTrail.emit(ptpId);
    }
  }

  readonly doiUrl = computed(() => {
    const doi = this.paper()?.doi?.trim();
    if (!doi) return null;
    if (doi.startsWith('http://') || doi.startsWith('https://')) return doi;
    if (DOI_REGEX.test(doi)) return `https://doi.org/${doi}`;
    return null;
  });

  readonly externalIdUrl = computed(() => {
    const id = this.paper()?.externalId;
    if (!id) return null;
    // Strip uppercase prefix like "BIBTEX:..."
    const m = id.match(/^([A-Z][A-Z_]+):(.*)/);
    const stripped = m ? m[2] : id;
    if (stripped.startsWith('http://') || stripped.startsWith('https://')) return stripped;
    return null;
  });

  readonly otherProjectsCount = computed(() => {
    const p = this.paper();
    return p ? Math.max(0, p.projectsLinkedCount - 1) : 0;
  });

  /** Normalize authors (which may be a JSON array string or already an array) for display. */
  readonly authorsDisplay = computed(() => {
    const a = this.paper()?.authors;
    if (!a) return '—';
    if (Array.isArray(a)) return a.join(', ');
    // Try to parse as JSON array
    try {
      const parsed = JSON.parse(a);
      if (Array.isArray(parsed)) return parsed.join(', ');
    } catch {
      // Not JSON, display as-is
    }
    return a;
  });

  readonly year = computed(() => {
    const d = this.paper()?.publicationDate;
    if (!d) return null;
    const date = new Date(d);
    return isNaN(date.getTime()) ? null : date.getFullYear();
  });

  private fetchPaper(): void {
    const projectId = this._projectId();
    const ptpId = this._paperToProjectId();
    if (!projectId || ptpId === null) return;

    this.loading.set(true);
    this.error.set(null);
    this.paper.set(null);

    this.http
      .get<PaperInfo>(`${this.globals.backendUri}/projects/${projectId}/paper-to-project/${ptpId}`)
      .subscribe({
        next: (data) => {
          this.paper.set(data);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err?.error?.message ?? err?.message ?? 'Failed to load paper details');
          this.loading.set(false);
        },
      });
  }

  close(): void {
    this._paperToProjectId.set(null);
    this.paper.set(null);
    this.error.set(null);
    this.closed.emit();
  }
}
